import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, onSnapshot, orderBy, where, addDoc, serverTimestamp } from "firebase/firestore";
import useCurrentUser from "../hooks/useCurrentUser";
import {
    MessageSquare,
    Plus,
    Search,
    Clock,
    CheckCircle2,
    AlertCircle,
    ChevronRight,
    LifeBuoy,
    Send,
    Tag
} from "lucide-react";

const TicketStatus = ({ status }) => {
    const styles = {
        open: "text-red-600 bg-red-50",
        in_progress: "text-blue-600 bg-blue-50",
        waiting_client: "text-amber-600 bg-amber-50",
        resolved: "text-emerald-600 bg-emerald-50",
        closed: "text-slate-500 bg-slate-50"
    };
    return (
        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${styles[status] || styles.open}`}>
            {status?.replace("_", " ")}
        </span>
    );
};
export default function MyTickets() {
    const { user, clientId, loading: authLoading } = useCurrentUser();
    const userId = user?.uid || "anonymous";
    const activeIdentifier = clientId || userId;

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Create Ticket Form State
    const [newTicket, setNewTicket] = useState({
        subject: "",
        category: "general",
        priority: "normal",
        message: ""
    });

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, "tickets"),
            where("companyId", "==", activeIdentifier)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ticketData = [];
            snapshot.forEach(doc => ticketData.push({ id: doc.id, ...doc.data() }));

            ticketData.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                return dateB - dateA;
            });

            setTickets(ticketData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [clientId]);

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject || !newTicket.message) return;

        try {
            // In a real app, the ticketNumber should be generated on the backend via transaction
            // For now, we'll use a placeholder or a simple random string for demo purposes
            // Better: use a temporary doc to get the next counter value

            const ticketDocData = {
                ticketNumber: `TG-${Math.floor(100000 + Math.random() * 900000)}`,
                userId,
                companyId: activeIdentifier,
                subject: newTicket.subject,
                category: newTicket.category,
                priority: newTicket.priority,
                status: "open",
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, "tickets"), ticketDocData);

            // Add first message
            await addDoc(collection(db, "ticketMessages"), {
                ticketId: docRef.id,
                senderId: userId,
                message: newTicket.message,
                timestamp: serverTimestamp()
            });

            // Add notification for Super Admin
            await addDoc(collection(db, "notifications"), {
                userId: "super_admin",
                title: "New Support Ticket",
                message: `Ticket ${ticketDocData.ticketNumber} opened by ${activeIdentifier}`,
                type: "alert",
                link: "/super-admin/tickets",
                read: false,
                createdAt: serverTimestamp()
            });

            setIsCreateModalOpen(false);
            setNewTicket({ subject: "", category: "general", priority: "normal", message: "" });
        } catch (error) {
            console.error("Error creating ticket:", error);
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-navy-900 p-8 rounded-3xl shadow-xl shadow-navy-900/20 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <LifeBuoy size={120} />
                </div>
                <div className="relative z-10">
                    <h1 className="text-3xl font-bold font-serif">Support Command Center</h1>
                    <p className="text-navy-200 mt-2 max-w-md">Need assistance with shipments, billing, or technical issues? Our global team is ready to help.</p>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="relative z-10 flex items-center gap-2 px-6 py-3 bg-gold-500 text-navy-900 rounded-xl font-bold hover:bg-gold-400 transition-all shadow-lg active:scale-95"
                >
                    <Plus size={20} /> Open New Ticket
                </button>
            </div>

            {/* Quick Filter */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl">
                        <AlertCircle size={24} />
                    </div>
                    <div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Open Issues</p>
                        <p className="text-2xl font-bold text-navy-900">{tickets.filter(t => t.status === 'open').length}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                        <MessageSquare size={24} />
                    </div>
                    <div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">In Progress</p>
                        <p className="text-2xl font-bold text-navy-900">{tickets.filter(t => t.status === 'in_progress').length}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                        <CheckCircle2 size={24} />
                    </div>
                    <div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Resolved</p>
                        <p className="text-2xl font-bold text-navy-900">{tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}</p>
                    </div>
                </div>
            </div>

            {/* Ticket List */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <h2 className="font-bold text-navy-900 flex items-center gap-2">
                        <Clock size={18} /> Recent Support Requests
                    </h2>
                </div>

                <div className="divide-y divide-slate-100">
                    {loading ? (
                        <div className="p-12 text-center text-slate-500">
                            <div className="w-8 h-8 border-4 border-navy-900/10 border-t-navy-900 rounded-full animate-spin mx-auto mb-4"></div>
                            Loading your tickets...
                        </div>
                    ) : tickets.length === 0 ? (
                        <div className="p-20 text-center">
                            <div className="w-20 h-20 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-6">
                                <LifeBuoy size={40} />
                            </div>
                            <h3 className="text-lg font-bold text-navy-900">No tickets found</h3>
                            <p className="text-slate-500 mt-2 mb-8">You haven't opened any support tickets yet.</p>
                            <button
                                onClick={() => setIsCreateModalOpen(true)}
                                className="px-6 py-2.5 bg-navy-900 text-white rounded-lg font-bold hover:bg-navy-800 transition-all"
                            >
                                Get Started
                            </button>
                        </div>
                    ) : (
                        tickets.map((ticket) => (
                            <div key={ticket.id} className="p-6 hover:bg-slate-50 transition-all cursor-pointer group flex items-center justify-between">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className={`p-3 rounded-2xl bg-slate-100 text-slate-400 group-hover:bg-navy-900 group-hover:text-white transition-all`}>
                                        <MessageSquare size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest font-mono">
                                                {ticket.ticketNumber}
                                            </span>
                                            <TicketStatus status={ticket.status} />
                                        </div>
                                        <h4 className="text-base font-bold text-navy-900 line-clamp-1">{ticket.subject}</h4>
                                        <div className="flex items-center gap-4 text-xs text-slate-400 font-medium">
                                            <span className="flex items-center gap-1"><Clock size={12} /> {ticket.createdAt?.toDate ? ticket.createdAt.toDate().toLocaleDateString() : 'Pending'}</span>
                                            <span className="flex items-center gap-1 capitalize"><Tag size={12} /> {ticket.category}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="hidden md:block text-right">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Priority</p>
                                        <p className={`text-xs font-bold uppercase ${ticket.priority === 'urgent' ? 'text-rose-600' : ticket.priority === 'high' ? 'text-amber-600' : 'text-blue-600'}`}>
                                            {ticket.priority}
                                        </p>
                                    </div>
                                    <ChevronRight className="text-slate-300 group-hover:text-navy-900 transition-all" size={20} />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Create Ticket Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-10 duration-400 transform">
                        <div className="p-8 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <div>
                                <h3 className="text-2xl font-bold text-navy-900 font-serif">Open Support Ticket</h3>
                                <p className="text-slate-500 text-sm mt-1">Provide as much detail as possible for faster resolution.</p>
                            </div>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="p-2 hover:bg-slate-200 rounded-full transition-all text-slate-400"
                            >
                                <Plus size={24} className="rotate-45" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateTicket} className="p-8 space-y-6">
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-1">Category</label>
                                        <select
                                            className="w-full p-3 bg-slate-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-navy-900/10 outline-none"
                                            value={newTicket.category}
                                            onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                                        >
                                            <option value="general">General Support</option>
                                            <option value="billing">Billing & Invoices</option>
                                            <option value="shipment">Shipment Issues</option>
                                            <option value="customs">Customs & Compliance</option>
                                            <option value="tech">Technical Problem</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-1">Priority</label>
                                        <select
                                            className="w-full p-3 bg-slate-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-navy-900/10 outline-none"
                                            value={newTicket.priority}
                                            onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                        >
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-1">Subject</label>
                                    <input
                                        type="text"
                                        placeholder="Briefly describe the issue"
                                        className="w-full p-3 bg-slate-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-navy-900/10 outline-none"
                                        value={newTicket.subject}
                                        onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-1">Detailed Message</label>
                                    <textarea
                                        placeholder="Describe your problem, include shipment numbers or invoice IDs..."
                                        rows={4}
                                        className="w-full p-3 bg-slate-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-navy-900/10 outline-none resize-none"
                                        value={newTicket.message}
                                        onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full py-4 bg-navy-900 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-xl shadow-navy-900/20 active:scale-[0.98]"
                            >
                                <Send size={18} /> Submit Ticket
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
